/**
 * TierContext - Provides tier usage, limits, and feature access
 *
 * This context provides:
 * - Current tier (free, pro, pro_plus)
 * - Limits for cards, storage, AI credits
 * - Current usage stats
 * - Feature access flags
 * - Helper methods like canCreateCard(), canUploadFile()
 *
 * Uses Convex reactive queries for real-time updates.
 */

import React, { createContext, useContext, useCallback, ReactNode, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@careercard/convex';
import { useAuth } from './AuthContext';
import { createLogger } from '../utils/logger-ssr-safe';

const logger = createLogger({ module: 'TierContext' });

// ============================================================================
// Types
// ============================================================================

export type SubscriptionTier = 'free' | 'pro' | 'pro_plus';

export interface TierLimits {
  cards: number;
  aiCreditsPerMonth: number;
  storageBytes: number;
  storageMB: number;
  storageGB: number;
  isUnlimitedCards: boolean;
}

export interface CardUsage {
  used: number;
  limit: number;
  remaining: number | null;
  percentUsed: number;
  isAtLimit: boolean;
  isUnlimited: boolean;
}

export interface StorageUsage {
  usedBytes: number;
  usedMB: number;
  limitBytes: number;
  limitMB: number;
  remainingBytes: number;
  percentUsed: number;
  isAtLimit: boolean;
  isWarning: boolean;
  isCritical: boolean;
}

export interface AiCreditsUsage {
  used: number;
  limit: number;
  remaining: number;
  percentUsed: number;
  isAtLimit: boolean;
  resetsAt: string;
}

export interface TierUsage {
  cards: CardUsage;
  storage: StorageUsage;
  aiCredits: AiCreditsUsage;
}

export interface TierFeatures {
  stakeholderViews: boolean;
  stakeholderConnections: boolean;
  reviewMetrics: boolean;
  aiCardImprovements: boolean;
  fileAttachments: boolean;
  versionHistory: boolean;
  talkingPoints: boolean;
  orgChart: boolean;
  canvasView: boolean;
  resumeTailoring: boolean;
  fullReviewGeneration: boolean;
  voiceToText: boolean;
}

export interface UpgradePromptFlags {
  cards: boolean;
  storage: boolean;
  aiCredits: boolean;
  anyLimit: boolean;
}

export interface TierUsageResponse {
  tier: SubscriptionTier;
  limits: TierLimits;
  usage: TierUsage;
  features: TierFeatures;
  shouldPromptUpgrade: UpgradePromptFlags;
}

// ============================================================================
// Context Value Interface
// ============================================================================

interface TierContextValue {
  // State
  loading: boolean;
  error: Error | null;
  tier: SubscriptionTier;
  limits: TierLimits | null;
  usage: TierUsage | null;
  features: TierFeatures | null;
  shouldPromptUpgrade: UpgradePromptFlags;
  lastFetched: number | null;

  // Methods
  refreshTierUsage: () => Promise<void>;
  /** Refresh tier data only if it's stale (no-op with Convex reactive queries) */
  refreshIfStale: () => Promise<void>;

  // Computed helpers for limit checks
  canCreateCard: () => boolean;
  canUploadFile: (fileSizeBytes: number) => boolean;
  canUseAiCredits: () => boolean;
  hasFeature: (feature: keyof TierFeatures) => boolean;
  /** Check if cached tier data is stale (always false with reactive queries) */
  isStale: () => boolean;

  // Usage display helpers
  getCardUsageText: () => string;
  getStorageUsageText: () => string;
  getAiCreditsUsageText: () => string;
}

// ============================================================================
// Default Values
// ============================================================================

const defaultUpgradePromptFlags: UpgradePromptFlags = {
  cards: false,
  storage: false,
  aiCredits: false,
  anyLimit: false,
};

// ============================================================================
// Context Creation
// ============================================================================

const TierContext = createContext<TierContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useTier = () => {
  const context = useContext(TierContext);
  if (!context) {
    throw new Error('useTier must be used within a TierProvider');
  }
  return context;
};

// Safe version that doesn't throw - returns default free tier values
// eslint-disable-next-line react-refresh/only-export-components
export const useTierSafe = () => {
  const context = useContext(TierContext);
  if (!context) {
    // Return default free tier values
    return {
      loading: false,
      error: null,
      tier: 'free' as SubscriptionTier,
      limits: null,
      usage: null,
      features: null,
      shouldPromptUpgrade: defaultUpgradePromptFlags,
      lastFetched: null,
      refreshTierUsage: async () => {},
      refreshIfStale: async () => {},
      canCreateCard: () => true,
      canUploadFile: () => true,
      canUseAiCredits: () => true,
      hasFeature: () => false,
      isStale: () => true,
      getCardUsageText: () => '',
      getStorageUsageText: () => '',
      getAiCreditsUsageText: () => '',
    };
  }
  return context;
};

// ============================================================================
// Provider Implementation
// ============================================================================

interface TierProviderProps {
  children: ReactNode;
}

export const TierProvider: React.FC<TierProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  // Use Convex reactive query for tier usage - only when authenticated
  // The query will automatically update when data changes
  const tierUsageData = useQuery(
    api.tier.getTierUsage,
    isAuthenticated ? {} : "skip"
  );

  // Derive loading state from query result
  const loading = isAuthenticated && tierUsageData === undefined;

  // Extract data from query result
  const tier: SubscriptionTier = tierUsageData?.tier ?? 'free';
  const limits: TierLimits | null = tierUsageData?.limits ?? null;
  const usage: TierUsage | null = tierUsageData?.usage ?? null;
  const features: TierFeatures | null = tierUsageData?.features ?? null;
  const shouldPromptUpgrade: UpgradePromptFlags = tierUsageData?.shouldPromptUpgrade ?? defaultUpgradePromptFlags;

  // Log when data is loaded
  if (tierUsageData) {
    logger.debug('Tier usage loaded from Convex:', { tier: tierUsageData.tier });
  }

  // ============================================================================
  // Refresh Methods (no-op with reactive queries, kept for API compatibility)
  // ============================================================================

  const refreshTierUsage = useCallback(async () => {
    // With Convex reactive queries, data is automatically updated
    // This method is kept for backward compatibility but is effectively a no-op
    logger.debug('refreshTierUsage called - Convex reactive queries handle updates automatically');
  }, []);

  const refreshIfStale = useCallback(async () => {
    // With Convex reactive queries, data is never stale
    // This method is kept for backward compatibility but is effectively a no-op
    logger.debug('refreshIfStale called - Convex reactive queries are always fresh');
  }, []);

  // ============================================================================
  // Helper Methods
  // ============================================================================

  const canCreateCard = useCallback((): boolean => {
    if (!usage?.cards) return true; // Allow if no usage data yet
    return !usage.cards.isAtLimit;
  }, [usage]);

  const canUploadFile = useCallback((fileSizeBytes: number): boolean => {
    if (!usage?.storage) return true; // Allow if no usage data yet
    return (usage.storage.usedBytes + fileSizeBytes) <= (limits?.storageBytes ?? 0);
  }, [usage, limits]);

  const canUseAiCredits = useCallback((): boolean => {
    if (!usage?.aiCredits) return true; // Allow if no usage data yet
    return !usage.aiCredits.isAtLimit;
  }, [usage]);

  const hasFeature = useCallback((feature: keyof TierFeatures): boolean => {
    if (!features) return false;
    return features[feature] === true;
  }, [features]);

  // ============================================================================
  // Staleness Helpers (always fresh with reactive queries)
  // ============================================================================

  const isStale = useCallback((): boolean => {
    // With Convex reactive queries, data is never stale
    return false;
  }, []);

  // ============================================================================
  // Usage Display Text Helpers
  // ============================================================================

  const getCardUsageText = useCallback((): string => {
    if (!usage?.cards || !limits) return '';

    if (usage.cards.isUnlimited) {
      return `${usage.cards.used} cards`;
    }

    return `${usage.cards.used} of ${limits.cards} cards`;
  }, [usage, limits]);

  const getStorageUsageText = useCallback((): string => {
    if (!usage?.storage || !limits) return '';

    // Use MB for display (more human-readable)
    return `${usage.storage.usedMB.toFixed(1)} MB of ${limits.storageMB} MB`;
  }, [usage, limits]);

  const getAiCreditsUsageText = useCallback((): string => {
    if (!usage?.aiCredits || !limits) return '';

    return `${usage.aiCredits.used} of ${limits.aiCreditsPerMonth} AI credits this month`;
  }, [usage, limits]);

  // ============================================================================
  // Context Value (memoized to prevent unnecessary re-renders)
  // ============================================================================

  const value: TierContextValue = useMemo(() => ({
    // State
    loading,
    error: null, // Convex handles errors via the query result
    tier,
    limits,
    usage,
    features,
    shouldPromptUpgrade,
    lastFetched: tierUsageData ? Date.now() : null,

    // Methods
    refreshTierUsage,
    refreshIfStale,

    // Limit check helpers
    canCreateCard,
    canUploadFile,
    canUseAiCredits,
    hasFeature,
    isStale,

    // Display text helpers
    getCardUsageText,
    getStorageUsageText,
    getAiCreditsUsageText,
  }), [
    loading,
    tier,
    limits,
    usage,
    features,
    shouldPromptUpgrade,
    tierUsageData,
    refreshTierUsage,
    refreshIfStale,
    canCreateCard,
    canUploadFile,
    canUseAiCredits,
    hasFeature,
    isStale,
    getCardUsageText,
    getStorageUsageText,
    getAiCreditsUsageText,
  ]);

  return (
    <TierContext.Provider value={value}>
      {children}
    </TierContext.Provider>
  );
};

export default TierContext;
